import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleNewsletter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();

    if (honeypot) {
      setFormStatus('success');
      setEmail('');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const res = await fetch('https://readdy.ai/api/form/d96glncut53jbls7knqg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: formData.get('email') as string }).toString(),
      });
      const responseText = await res.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch (_) { /* ignore parse error */ }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.includes('spam') || responseText.includes('spam')) {
          setFormError('Wystąpił błąd. Spróbuj ponownie później.');
        } else {
          setFormError(serverMsg || 'Wystąpił nieoczekiwany błąd. Spróbuj ponownie.');
        }
        setFormStatus('error');
      }
    } catch (_err) {
      setFormError('Nie udało się wysłać. Sprawdź połączenie i spróbuj ponownie.');
      setFormStatus('error');
    }
  };

  const halfLen = Math.ceil(categories.length / 2);
  const col1 = categories.slice(0, halfLen);
  const col2 = categories.slice(halfLen);

  return (
    <footer className="bg-background-50 border-t border-background-200">
      <div className="w-full max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 mb-16">
          <div className="lg:col-span-2">
            <Link to="/" className="no-underline cursor-pointer">
              <span className="text-3xl font-bold font-heading text-foreground-950">
                Kakobuy<span className="text-primary-500">Finds</span>
              </span>
            </Link>
            <p className="mt-4 text-sm text-foreground-600 leading-relaxed max-w-sm">
              Niezależna platforma odkrywania produktów Kakobuy. Największa baza spreadsheetów w Polsce. Pomagamy Ci znaleźć najlepsze okazje zakupowe.
            </p>
            <div className="mt-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-foreground-500 mb-3">Newsletter</p>
              <form onSubmit={handleNewsletter} data-readdy-form="">
                <input
                  type="text"
                  name="website_alt"
                  className="form-honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Twój adres email"
                    required
                    className="flex-1 px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-full outline-none focus:border-primary-400 transition-colors text-foreground-800"
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
                  >
                    {formStatus === 'submitting' ? 'Wysyłanie...' : 'Zapisz Się'}
                  </button>
                </div>
                {formStatus === 'success' && (
                  <p className="mt-2 text-xs text-green-600">Dziękujemy za zapisanie się!</p>
                )}
                {formStatus === 'error' && formError && (
                  <p className="mt-2 text-xs text-red-500">{formError}</p>
                )}
              </form>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-500 mb-4">Kategorie</h4>
            <div className="w-8 h-px bg-background-300 mb-4"></div>
            <ul className="space-y-2.5">
              {col1.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
                  >
                    {cat.namePl}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-500 mb-4">&nbsp;</h4>
            <div className="w-8 h-px bg-background-300 mb-4"></div>
            <ul className="space-y-2.5">
              {col2.map((cat) => (
                <li key={cat.id}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors cursor-pointer"
                  >
                    {cat.namePl}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold uppercase tracking-widest text-foreground-500 mb-4">Poradniki</h4>
            <div className="w-8 h-px bg-background-300 mb-4"></div>
            <ul className="space-y-2.5">
              <li>
                <Link to="/artykul/kakobuy-sheet-kompletny-przewodnik" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors">
                  Kakobuy Sheet – Przewodnik
                </Link>
              </li>
              <li>
                <Link to="/artykul/cssbuy-vs-kakobuy-porownanie-spreadsheet" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors">
                  CSSBuy vs Kakobuy – Porównanie
                </Link>
              </li>
              <li>
                <Link to="/artykul/kakobuy-bezpieczne-zakupy-poradnik" className="text-sm text-foreground-600 hover:text-foreground-950 transition-colors">
                  Bezpieczne Zakupy – Poradnik
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between pt-8 border-t border-background-200 gap-4">
          <p className="text-xs text-foreground-500">
            &copy; 2026 KakobuyFinds. Wszelkie prawa zastrzeżone.
          </p>
          <div className="flex items-center gap-4">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center text-foreground-500 hover:text-foreground-800 transition-colors cursor-pointer">
              <i className="ri-external-link-line"></i>
            </a>
            <span className="w-6 h-6 flex items-center justify-center text-foreground-400 cursor-default">
              <i className="ri-twitter-x-line"></i>
            </span>
            <span className="w-6 h-6 flex items-center justify-center text-foreground-400 cursor-default">
              <i className="ri-facebook-line"></i>
            </span>
            <span className="w-6 h-6 flex items-center justify-center text-foreground-400 cursor-default">
              <i className="ri-instagram-line"></i>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}